export const DETAIL_PANES = ['summary', 'log', 'excess'] as const;

export const DETAIL_PANE_LABELS = {
  summary: 'Summary',
  log: 'Time log',
  excess: 'Excess hours',
} as const;

// A person has no bill of their own.
export const MEMBER_DETAIL_PANES = ['summary', 'log'] as const;

export const LOG_GRID_PREFIX = 'log_';

export const BILLABLE_OPTIONS = [
  { value: 'billable', label: 'Billable' },
  { value: 'nonbillable', label: 'Non-billable' },
];

export const ALL_PORTFOLIOS = 'all';

export const CLIENT_GRID_PREFIX = 'cli_';

export const ROLE_LABELS = { cfo: 'CFO', dataTeam: 'Data team' } as const;

export const ROLE_BAR_CLASS = {
  cfo: { logged: 'bg-capacity-cfo' },
  dataTeam: { logged: 'bg-capacity-data' },
} as const;

// On the capacity ring, committed hours not logged yet wear the same hue faded:
// the ring's full turn is the capacity, so the faded arc is not the tick again.
export const ROLE_RING_CLASS = {
  cfo: {
    logged: 'stroke-capacity-cfo',
    remaining: 'stroke-capacity-cfo/30',
    faded: 'bg-capacity-cfo/30',
  },
  dataTeam: {
    logged: 'stroke-capacity-data',
    remaining: 'stroke-capacity-data/30',
    faded: 'bg-capacity-data/30',
  },
} as const;

export const ROLE_DOT_CLASS = { cfo: 'bg-capacity-cfo', dataTeam: 'bg-capacity-data' } as const;

export const COMPOSITION_KEYS = ['cfo', 'dataTeam'] as const;

export const COMPOSITION_LABELS = {
  cfo: 'CFO',
  dataTeam: 'Data team',
} as const;

export const COMPOSITION_CLASS = {
  cfo: 'bg-capacity-cfo',
  dataTeam: 'bg-capacity-data',
} as const;

export const BUDGET_BUCKETS = [
  { key: 'fixed', label: 'Fixed' },
  { key: 'variable', label: 'Variable' },
] as const;

// Plain-language answers to "what is this?" for the figures a new CFO meets
// first on Capacity.
export const TERM_HELP = {
  engagement:
    'The hours your clients pay for this month: their fixed retainer plus any variable hours.',
  leverage:
    'The share of the engagement handed to the Data team. Every hour not handed to the Data team is the CFO’s, so the higher it is, the more of the work the CFO delegates.',
  logged:
    'Every hour logged in ClickUp this month on this portfolio’s clients, by the CFO and the Data team alike.',
  month:
    'How far into the month we are: weekdays so far, today included, out of all the weekdays in the month.',
  forecast:
    'Where the month is likely to end up, worked out for fixed and variable hours separately. Each takes the higher of two numbers: the hours logged plus the hours still estimated on open tasks, or the hours logged so far carried on at this month’s pace. Pace only counts once a quarter of the month’s weekdays are gone; before that, a few days of logging would project a misleading total, so the forecast is logged plus estimated. The total adds both buckets; its projected over adds each bucket’s, so spare fixed hours never hide variable ones going over.',
  overage:
    'Hours logged above what the client pays for. Fixed and variable hours are counted separately, so going over on one is not covered by the other.',
  projectedOver:
    'How far each bucket is heading over its budget by the end of the month, from its forecast. Fixed and variable are projected separately. In the first quarter of the month’s weekdays it comes from logged plus estimated hours only, without pace. Click it to see how the forecast is worked out.',
  paceNotYet:
    'Pace counts once a quarter of the month’s weekdays are gone. Until then the forecast is logged plus estimated.',
} as const;

// Keyed by column id, so a column only gets an explanation where one is written.
export const CLIENT_COLUMN_HELP: Partial<Record<string, string>> = {
  actual:
    'Fixed and variable hours logged on the client this month, each against its own budget, the solid tick. The fill is split by who logged them: the CFO’s colour first, then the Data team’s.',
  plan_fixed:
    'The client’s fixed hours, the retainer. A change here counts from the month on screen and every later month keeps it until it is changed again; earlier months keep what they had. Last month’s figure shows below when it differs, and so do any extra hours for this month only.',
  plan_variable:
    'The client’s variable hours, the billable side. A change here counts from the month on screen and every later month keeps it until it is changed again; earlier months keep what they had. Last month’s figure shows below when it differs, and so do any extra hours for this month only.',
  plan_team:
    'Fixed / variable hours of this client handed to someone else, usually the Data team. Add someone, click a name to change their hours, or remove them. Whatever is handed over comes out of what the CFO keeps.',
  plan_cfo:
    'Fixed / variable hours the CFO holds: the client’s hours this month, extra hours included, less what was handed to the Data team.',
  overage: TERM_HELP.overage,
  projectedOver: TERM_HELP.projectedOver,
};

export const FORECAST_ROWS = [
  { key: 'budget', label: 'Budget' },
  { key: 'logged', label: 'Logged' },
  { key: 'remaining', label: '+ Still estimated' },
  { key: 'pace', label: 'At this month’s pace' },
  { key: 'forecast', label: 'Forecast' },
  { key: 'projectedOver', label: 'Projected over' },
] as const;

export const UNCATEGORIZED = 'Uncategorized';

export const EXCESS_FORMATS = ['excel', 'pdf'] as const;

export const EXCESS_FORMAT_LABELS = { excel: 'Excel', pdf: 'PDF' } as const;
